import Numeric from "@/core/types/Numeric";

/**
 * Утилиты для валидации данных форм
 */
class ValidatorUtils {
	/**
	 * Стандартные сообщения об ошибках валидации
	 */
	static readonly MESSAGES = {
		default: "Некорректное значение",
		required: "Поле обязательно для заполнения",
		email: "Некорректный email",
		min: "Поле должно быть больше или равно",
		max: "Поле должно быть меньше или равно",
		minLength: "Длина поля должна быть больше",
		maxLength: "Длина поля должна быть меньше",
		options: "Данного значения нет среди допустимых",
		name: "Поле может содержать только буквы, пробелы и дефисы",
		phone: "Телефон может содержать только цифры, пробелы и символы: «( )», «-», «+»",
	};

	/**
	 * Проверяет что значение не пустое
	 */
	static required(value: any): boolean {
		if (value === undefined || value === null) {
			return false;
		}

    // на случай чтобы не прошло значение с пробелами, по факту не пустое, но нам такое не надо
    if (typeof value === "string") {
      return value.trim().length > 0;
    }

    if (Array.isArray(value)) {
      return value.length > 0;
    }

    // Numeric-типы (Qty, Weight, Price, Dimension и т.д.) имеют собственное
    // понятие "пусто" — не null/undefined/'', а isEmpty() (внутренний Decimal
    // равен NaN). Без этой проверки required не
    // ловил незаполненные числовые поля: сам объект Qty — не null и не '',
    // значит проходил как "заполнено", даже если пользователь ничего не ввёл.
    if (value instanceof Numeric) {
      return value.isEmpty() === false;
    }

		return true;
	}

	/**
	 * Проверяет корректность имени (только буквы, пробелы и дефисы)
	 */
	static name(value: string): boolean {
		if (!value) return true;
		const re = /^[а-яА-ЯёЁa-zA-Z\s-]+$/;
		return re.test(value);
	}

	/**
	 * Проверяет корректность email адреса
	 */
	static email(value: string): boolean {
		const re =
			/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
		return re.test(value);
	}

	/**
	 * Проверяет корректность телефона
	 */
	static phone(value: string): boolean {
		if (!value) return true;

		const re = /^(?:\+[0-9]|\d)(?:[0-9\s-]*|\([0-9\s-]+\)[0-9\s-]*)$/;
		if (!re.test(value)) return false;

		const digitsCount = value.replace(/\D/g, '').length;
		return !(digitsCount < 5 || digitsCount > 15);
	}

	/**
	 * Проверяет что значение больше или равно минимуму.
	 * Numeric сравниваем его методами (>= это «не меньше» = !lessThan),
	 * иначе valueOf() бросил бы при value >= minimum. Пустой Numeric
	 * (isEmpty) границей не бракуем — это забота required.
	 */
	static min(value: number | Numeric | null, minimum: number): boolean {
		if (value === null) return true;
		if (value instanceof Numeric) {
			if (value.isEmpty()) return true;
			return !value.lessThan(minimum);
		}
		if (!this.required(value) || !this.required(minimum)) {
			return false;
		}
		return value >= minimum;
	}

	/**
	 * Проверяет что значение меньше или равно максимуму.
	 * Numeric сравниваем его методами (<= это «не больше» = !greaterThan).
	 */
	static max(value: number | Numeric | null, maximum: number): boolean {
		if (value === null) return true;
		if (value instanceof Numeric) {
			if (value.isEmpty()) return true;
			return !value.greaterThan(maximum);
		}
		if (!this.required(value) || !this.required(maximum)) {
			return false;
		}
		return value <= maximum;
	}

	/**
	 * Проверяет что длина значения больше или равна минимальной
	 */
	static minLength(value: any, length: any): boolean {
		const _value = typeof value === "number" ? value.toString() : value;
		if (typeof length !== "number" || (typeof _value !== "string" && !Array.isArray(_value))) {
			return false;
		}
		return _value.length >= length;
	}

	/**
	 * Проверяет что длина значения меньше или равна максимальной
	 */
	static maxLength(value: any, length: any): boolean {
		if (value === null) return true;
		const _value = typeof value === "number" ? value.toString() : value;
		if (typeof length !== "number" || (typeof _value !== "string" && !Array.isArray(_value))) {
			return false;
		}
		return _value.length <= length;
	}

	/**
	 * Проверяет что значение присутствует в списке допустимых опций.
	 * Поддерживает как одиночный, так и множественный выбор.
	 */
	static options(value: any, options: any[]): boolean {
		// Т.к. search-function не использует option,
		// но мы получаем ошибку из-за того что не можем сравнить value с options,
		// поэтому если нет опций или список пуст, то пропускаем валидацию
		if (!options || options.length === 0) {
			return true;
		}
		if (!value) return true;

		// Множественный выбор
		if (Array.isArray(value)) {
			return value.every((val) => options.some((option) => option.id === val.id));
		}

		// Одиночный выбор
		return options.some((option) => option.id === value.id);
	}
}

export default ValidatorUtils;
